import { CodeSnippetExecState } from '@devbookhq/sdk'
import {
  CodeSnippetExtendedState,
  CodeSnippetState,
  useSharedSession,
} from '@devbookhq/react'
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Terminal,
  TerminalHandler,
} from '@devbookhq/terminal'
import Splitter, { GutterTheme } from '@devbookhq/splitter'

import { Example } from 'utils/examplesContent'
import { TOCPage } from 'components/TOCSidebar/TOCPage'
import Code, {
  Handler as CodeHandler,
  Props as CodeProps,
} from 'components/Code'
import SessionAsleepScreen from 'components/SessionAsleepScreen'

import { Handler } from '../Handler'
import { analytics } from 'utils/analytics'
import { rootdir } from 'utils/constants'
import DevTools from './DevTools'
import ExampleExecutionScreen from 'components/ExampleExecutingScreen'
import useTerminalOutputAnalytics from 'utils/analytics/useTerminalOutputAnalytics'

export interface Props {
  example: Example
  category: TOCPage
  subcategory: TOCPage
  onCodeChange: CodeProps['onCodeChange']
  onWakeUpSessionClick: (e: any) => void
  onRunClick: () => void
  onStopClick: () => void
}

function DesktopContent({
  example,
  onCodeChange,
  onWakeUpSessionClick,
  onRunClick,
  onStopClick,
}: Props, ref: ForwardedRef<Handler>) {
  const {
    state: sessionState,
    session,
  } = useSharedSession()
  const terminalRef = useRef<TerminalHandler>(null)
  const codeEditorRef = useRef<CodeHandler>(null)
  const [cmdState, setCmdState] = useState<CodeSnippetExecState>(CodeSnippetExecState.Stopped)
  const [splitSizes, setSplitSizes] = useState([60, 40])
  const executionState = useMemo<CodeSnippetState>(() => {
    switch (sessionState) {
    case 'closed':
      return CodeSnippetExecState.Stopped
    case 'opening':
      return CodeSnippetExtendedState.Loading
    case 'open':
      return cmdState
    }
  },
  [cmdState, sessionState])

  const handleTerminalUserLine = useCallback((line: string) => {
    const [, cmd] = line.split('$')
    if (!cmd) return
    const trimmedCmd = cmd.trim()
    if (trimmedCmd.length > 0) {
      analytics.track('terminal line written', { cmd: trimmedCmd })
    }
  }, [])

  const handleTerminalCopy = useCallback((selection: string) => {
    if (selection.length > 0) {
      analytics.track('terminal selection copied', { selection })
    }
  }, [])

  const { handleOutput } = useTerminalOutputAnalytics()

  useImperativeHandle(ref,
    () => ({
      codeEditor() {
        return codeEditorRef.current
      },
      terminal() {
        return terminalRef.current
      },
    }))

  return (
    <div className="
      flex-1
      flex
      flex-row
      overflow-y-auto
      border-l
      border-gray-300
    ">
      <div className="
        flex-1
        flex
        flex-row
      ">
        <Splitter
          classes={['flex', 'flex']}
          gutterClassName="bg-white z-40"
          gutterTheme={GutterTheme.Light}
          initialSizes={splitSizes}
          onResizeFinished={(_, newSizes) => setSplitSizes(newSizes)}
        >
          <Code
            csState={executionState}
            docsLink={example.docsLink}
            docsLinkTitle={example.docsLinkTitle}
            exampleTitle={example.title}
            ref={codeEditorRef}
            tabs={example.tabs}
            onCodeChange={onCodeChange}
            onRunClick={onRunClick}
            onStopClick={onStopClick}
          />
          <div className="
            flex-1
            flex
            flex-col
            relative
          ">
            <SessionAsleepScreen
              isHidden={sessionState !== 'closed'}
              onWakeUpClick={onWakeUpSessionClick}
            />
            <ExampleExecutionScreen
              state={cmdState}
              stop={onStopClick}
            />
            <DevTools
              tools={[
                {
                  title: 'Terminal',
                  element: (
                    <Terminal
                      ref={terminalRef}
                      rootdir={rootdir}
                      session={session}
                      canStartTerminalSession
                      isReadOnly
                      onCopy={handleTerminalCopy}
                      onLine={handleTerminalUserLine}
                      onOutput={handleOutput}
                      onRunningCmdChange={setCmdState}
                    />
                  ),
                },
              ]}
            />
          </div>
        </Splitter>
      </div>
    </div>
  )
}

const ExampleDesktopContent = forwardRef<Handler, Props>(DesktopContent)

ExampleDesktopContent.displayName = 'ExampleDesktopContent'

export default ExampleDesktopContent
